import { ActionTree, MutationTree, GetterTree } from 'vuex'
import moment from 'moment'

export interface Permissions {
    add_range: boolean
    change_range: boolean
    delete_range: boolean
    add_point: boolean
    change_point: boolean
    delete_point: boolean
    falconio_import: boolean
}

export interface UserSettings {
    notification_interval: number | null
    full_name: string
    permissions: Permissions
}

export interface Brand {
    id: number
    title: string
    logo: string
    index: number
}

export interface Channel {
    id: number
    title: string
    icon_black: string
    icon_white: string
    type: string
    index: number
    brand_id: number
}

export interface Tag {
    id: number
    name: string
}

export interface VisibleTag extends Tag {
    id: number
    name: string
    count: number
}

export interface SpecialDate {
    id: number
    title: string
    type: string
    date: Date
    color: string
}

export interface Viewport {
    start: Date,
    end: Date,
    tags: VisibleTag[],
}

export interface Filter {
    types: string[]
    tag: number | undefined
    campaign: number | undefined
    channels: number[]
    brands: number[]
}

export interface State {
    loading: boolean
    userSettings: UserSettings
    brands: Brand[]
    channels: Channel[]
    channelMap: Map<number, Channel>
    tags: Tag[]
    specialDates: SpecialDate[]
    campaigns: Campaign[]
    campaignMap: Map<number, Campaign>
    ranges: Range[]
    points: Point[]
    viewport: Viewport
    filter: Filter
}

export interface Media {
    id: string | undefined
    upload_id: string | undefined
    image_url: string
    video_url: string
}

export interface Campaign {
    id: number | null
    title: string
    color: string
    brand_id: number
}

export interface Range {
    id: number | null
    title: string
    brand_id: number | null
    belongs_to: number[]
    start_date: string | null
    start_time: string | null
    end_date: string | null
    end_time: string | null
    text: string
    tags: number[]
    channels: number[]
    campaign_id: number | null
    sponsored_post: boolean
    comment: string
    medias: Media[]
    falconio_id: string | null
    subscribed: boolean
}

export interface Point {
    id: number | null
    type: string
    title: string
    brand_id: number | null
    belongs_to: number[]
    campaign_id: number | null
    date: string | null
    time: string | null
    subscribed: boolean
}

export interface Change {
    id: number
    type: string
    // FIXME
}

const emptyPermissions = () => ({
    add_range: false,
    change_range: false,
    delete_range: false,
    add_point: false,
    change_point: false,
    delete_point: false,
    falconio_import: false,
})

const emptyState = () => ({
    loading: false,
    userSettings: {
        notification_interval: null,
        full_name: '',
        permissions: emptyPermissions(),
    },
    brands: [],
    channels: [],
    channelMap: new Map(),
    tags: [],
    specialDates: [],
    campaigns: [],
    campaignMap: new Map(),
    ranges: [],
    points: [],
    viewport: {
        start: new Date(),
        end: new Date(),
        tags: [],
    },
    filter: {
        types: [],
        tag: undefined,
        campaign: undefined,
        brands: [],
        channels: [],
    },
}) as State

export const mutations: MutationTree<State> = {
    setLoading(state, loading) {
        state.loading = loading
    },
    setUserSettings(state, data: {notification_interval: number, full_name: string, permissions: string[]}) {
        const userSettings: UserSettings = {
            notification_interval: data.notification_interval,
            full_name: data.full_name,
            permissions: {
                add_range: data.permissions.indexOf('timeline.add_range') >= 0,
                change_range: data.permissions.indexOf('timeline.change_range') >= 0,
                delete_range: data.permissions.indexOf('timeline.delete_range') >= 0,
                add_point: data.permissions.indexOf('timeline.add_point') >= 0,
                change_point: data.permissions.indexOf('timeline.change_point') >= 0,
                delete_point: data.permissions.indexOf('timeline.delete_point') >= 0,
                falconio_import: data.permissions.indexOf('timeline.falconio_import') >= 0,
            },
        }
        state.userSettings = userSettings
    },
    setBrands(state, brands: Brand[]) {
        state.brands = brands
    },
    setCampaigns(state, campaigns: Campaign[]) {
        state.campaigns = campaigns
        const campaignMap = new Map<number, Campaign>()
        campaigns.forEach((g) => {
            campaignMap.set(g.id!, g)
        })
        state.campaignMap = campaignMap
    },
    setChannels(state, channels: Channel[]) {
        state.channels = channels
        const channelMap = new Map<number, Channel>()
        channels.forEach((c) => {
            channelMap.set(c.id, c)
        })
        state.channelMap = channelMap
    },
    setSpecialDates(state, specialDates: SpecialDate[]) {
        state.specialDates = specialDates
    },
    setTags(state, tags: Tag[]) {
        state.tags = tags
    },
    setRanges(state, ranges: Range[]) {
        state.ranges = ranges
    },
    setPoints(state, points: Point[]) {
        state.points = points
    },
    clear(state) {
        Object.assign(state, emptyState())
    },
    updateViewport(state, data) {
        Object.assign(state.viewport, data)
    },
    updateFilter(state, filter) {
        Object.assign(state.filter, filter)
    },
}

function getChannelIcons(state: State, channelIds: number[]) {
    return channelIds.map((channelId) => {
        const channel = state.channelMap.get(channelId)
        if (channel === undefined) {
            return ''
        }
        return `<img class="icon" src="${channel.icon_white}">`
    }).join('')
}

function getSponsoredPostIcon(range: Range) {
    if (range.sponsored_post) {
        return '<strong>€</strong>&nbsp;'
    } else {
        return ''
    }
}

function getTypeDisplay(type: string) {
    switch (type) {
        case 'obn': return 'OBN'
        case 'ocd': return 'OCD'
        case 'milestone': return 'Milestone'
        case 'deadline': return 'Deadline'
        default: return type
    }
}

export const actions: ActionTree<State, any> = {
    async load(context) {
        context.commit('setLoading', true)
        // FIXME how to parallelize this without floating promises?
        await context.dispatch('loadUserSettings')
        await context.dispatch('loadBrands')
        await context.dispatch('loadChannels')
        await context.dispatch('loadTags')
        await context.dispatch('loadEntries')
        context.commit('setLoading', false)
    },
    async loadUserSettings(context) {
        const response = await context.dispatch('fetch', 'user_settings')
        context.commit('setUserSettings', response.data)
    },
    async updateUserSettings(context, settings: UserSettings) {
        const response = await context.dispatch('api/patch',
            {
                path: 'user_settings', data: {
                    notification_interval: settings.notification_interval,
                },
            },
            { root: true },
        )
        context.commit('setUserSettings', response.data)
    },
    async loadBrands(context) {
        const response = await context.dispatch('fetch', 'brand')
        const brandDatas: Brand[] = response.data
        const brands = brandDatas.map(brandData => ({
            ...brandData,
            visible: true,
        }))
        context.commit('setBrands', brands)
    },
    async loadChannels(context) {
        const response = await context.dispatch('fetch', 'channel')
        const channelDatas: Channel[] = response.data
        const channels = channelDatas.map(channelData => ({
            ...channelData,
            visible: true,
        }))
        context.commit('setChannels', channels)
    },
    async loadTags(context) {
        const response = await context.dispatch('fetch', 'tag')
        const tags: Tag[] = response.data
        context.commit('setTags', tags)
    },
    async loadEntries(context) {
        await context.dispatch('loadSpecialDates')
        await context.dispatch('loadCampaigns')
        await context.dispatch('loadRanges')
        await context.dispatch('loadPoints')
    },
    async loadSpecialDates(context) {
        const response = await context.dispatch('fetch', 'special-date')
        context.commit('setSpecialDates', response.data)
    },
    async loadCampaigns(context) {
        const response = await context.dispatch('fetch', 'campaign')
        context.commit('setCampaigns', response.data)
    },
    async loadRanges(context) {
        const response = await context.dispatch('fetch', 'range')
        context.commit('setRanges', response.data)
    },
    async loadPoints(context) {
        const response = await context.dispatch('fetch', 'point')
        context.commit('setPoints', response.data)
    },
    fetch(context, type) {
        return context.dispatch(
            'api/get',
            { path: type },
            { root: true },
        )
    },
    async setViewport(context, { start, end }) {
        context.commit('updateViewport', { start, end })
        const response = await context.dispatch(
            'api/get',
            { path: 'stats', params: { start, end } },
            { root: true },
        )
        context.commit('updateViewport', { tags: response.data.tags })
    },
    async updateFilter(context, filter) {
        context.commit('updateFilter', filter)
    },
    async importFalconIo(context, { file }) {
        try {
            const form = new FormData()
            form.append('file', file, file.name)
            const response = await context.dispatch('api/post', {
                path: 'falcon.io/import',
                data: form,
                busy: true,
            }, { root: true })
            context.dispatch('load')
        } catch (e) {
            context.commit('api/error', {
                // FIXME add better error reporting
                message: 'An error occured',
            }, { root: true })
        }
    },
}

export const getters: GetterTree<State, any> = {
    items(state) {
        const filterType = new Set(state.filter.types)
        let filterTypeFunc: (type: string) => boolean
        if (filterType.size > 0) {
            filterTypeFunc = (type) => filterType.has(type)
        } else {
            filterTypeFunc = () => true
        }
        const filterTag = state.filter.tag
        let filterTagFunc: (tags: number[]) => boolean
        if (filterTag) {
            filterTagFunc = tags => tags.find(t => t === filterTag) !== undefined
        } else {
            filterTagFunc = () => true
        }
        const filterCampaign = state.filter.campaign
        let filterCampaignFunc: (campaign: number | null) => boolean
        if (filterCampaign) {
            filterCampaignFunc = campaign => campaign === filterCampaign
        } else {
            filterCampaignFunc = () => true
        }
        const filterBrands = new Set(state.filter.brands)
        let filterBrandFunc: (brand: number) => boolean
        if (filterBrands.size > 0) {
            filterBrandFunc = brand => filterBrands.has(brand)
        } else {
            filterBrandFunc = () => true
        }
        const filterChannels = new Set(state.filter.channels)
        let filterChannelsFunc: (channels: number[]) => boolean
        if (filterChannels.size > 0) {
            filterChannelsFunc = (channels) => channels
                .map(c => filterChannels.has(c))
                .reduce((x, s) => x || s, false)
        } else {
            filterChannelsFunc = () => true
        }
        return [
            ...state.specialDates.map((x) => ({
                _type: 'special-date',
                _obj: x,
                type: 'background',
                id: `s${x.id}`,
                start: x.date,
                end: moment.utc(x.date).add(1, 'day'),
                content: `<div class="label">${x.title}</div>`,
                className: `special-date s${x.id}`,
            })),
            ...filterTypeFunc('range') ? state.ranges
                .filter((x) => (
                    filterTagFunc(x.tags) &&
                    filterCampaignFunc(x.campaign_id) &&
                    filterBrandFunc(x.brand_id!) &&
                    filterChannelsFunc(x.channels)
                ))
                .map((x) => ({
                    _type: 'timeframe',
                    _obj: x,
                    type: 'range',
                    group: x.brand_id,
                    id: `r${x.id}`,
                    content: `${getSponsoredPostIcon(x)}${getChannelIcons(state, x.channels)}${x.title}`,
                    start: x.start_date,
                    end: x.end_date! > x.start_date!
                        ? x.end_date
                        : moment(x.start_date!).add(1, 'day').format('YYYY-MM-DD'),
                    title: `${x.start_date} – ${x.end_date}`,
                    className: `c${x.campaign_id}`,
                })) : [],
            ...state.points
                .filter((x) => (
                    filterTypeFunc(`point/${x.type}`) &&
                    filterCampaignFunc(x.campaign_id)
                ))
                .map((x) => ({
                    _type: 'point',
                    _obj: x,
                    type: 'point',
                    group: x.brand_id,
                    id: `p${x.id}`,
                    content: `${getTypeDisplay(x.type)} ${x.title}`,
                    start: x.date,
                    className: `${x.type}${x.campaign_id ? ` c${x.campaign_id}` : ''}`,
                    title: `${x.date}`,
                })),
        ]
    },
}

export const timeline = {
    namespaced: true,
    state: emptyState(),
    mutations,
    actions,
    getters,
}



// WEBPACK FOOTER //
// ./src/store/timeline.ts